import { Plus } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/app/components/ui/dialog";
import type { LanguageItem as LanguageItemData, ResumeContextValue } from "@/app/types/resume";

import tips from "../../../../assets/copy/tips";
import ErrorToast from "../../../../components/ErrorToast";
import Spinner from "../../../../components/Spinner";
import Tips from "../../../../components/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { Language } from "../../../../models/resume/language";
import { languageRowSchema } from "../../../../schemas/resume/languages";
import { StaticValue } from "../../../../seed/constants";
import mapping from "../../../../seed/metadata/filemapping";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import LanguageItem from "./components/LanguageItem";

interface PatchError {
	response: { data: { message: string | string[] } };
}

const BLANK_LANGUAGE: LanguageItemData = { language: "", proficiency: "" };

const isBlank = (item: LanguageItemData) => item.language.trim() === "" && item.proficiency.trim() === "";

const Languages = () => {
	const [cookies] = useCookies(["resume"]);
	const navigate = useNavigate();
	const languages: string[] = mapping["languages"][0].languages;
	const staticValue = new StaticValue();
	const staticValues = staticValue.getDefaultValues();
	const patchSection = usePatchResumeSection();

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;
	const [serverErrors, setServerErrors] = useState<string[]>([]);
	const [rowErrors, setRowErrors] = useState<string[]>([]);

	const [showUnsavedChangesWarning, setShowUnsavedChangesWarning] = useState(false);

	const [userLanguages, setUserLanguages] = useState<LanguageItemData[]>([BLANK_LANGUAGE]);

	const updateLanguagesData = (index: number, data: { language: string; proficiency: string }) => {
		setUserLanguages((previous) => previous.map((language, i) => (i === index ? data : language)));
		setRowErrors((previous) => previous.map((error, i) => (i === index ? "" : error)));
	};

	// With nothing saved the reader used to land on a bare "Add another language"
	// CTA — a screen that asks for languages but offers nowhere to type one. Open a
	// row for the first language and let the CTA add the ones after it.
	useEffect(() => {
		const saved = resumeData.extras.languages;
		setUserLanguages(saved.length > 0 ? saved : [BLANK_LANGUAGE]);
	}, [resumeData.extras.languages]);

	const onPrev = () => {
		const languages = resumeData.extras.languages.filter(
			(item1) =>
				!userLanguages.some((item2) =>
					Object.keys(item1).every(
						(key) => item2[key as keyof LanguageItemData] === item1[key as keyof LanguageItemData],
					),
				),
		);
		setResumeData((prevState) => {
			return {
				...prevState,
				extras: {
					...prevState.extras,
					languages: languages,
				},
			};
		});

		navigate("/resume/extras");
	};

	const onNext = () => {
		const nonBlankLanguages = userLanguages.filter((item) => !isBlank(item));
		const results = nonBlankLanguages.map((item) => languageRowSchema.safeParse(item));
		setRowErrors(
			userLanguages.map((item) => {
				const nonBlankIndex = nonBlankLanguages.indexOf(item);
				return nonBlankIndex !== -1 && !results[nonBlankIndex].success ? "Pick a language and a proficiency" : "";
			}),
		);
		if (results.some((result) => !result.success)) return;

		const langPayload = new Language(nonBlankLanguages);
		const langReq = langPayload.getLanguageRequest();
		if (nonBlankLanguages.length === 0) langReq.applicable = false;

		const resumeId = cookies[staticValues.localStorageResumeId];

		setResumeData((previous) => ({
			...previous,
			extras: { ...previous.extras, languages: nonBlankLanguages },
		}));
		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.LANGUAGES, data: langReq } as never, {
			onSuccess: successPatchLanguage,
			onError: errorPatchLanguage,
		});
	};

	function successPatchLanguage(_ledger: Record<string, unknown>) {
		navigate("/resume/extras");
	}

	function errorPatchLanguage(data: Error) {
		const serverResponse = (data as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	const deleteLangHandler = (index: number) => {
		const languages = userLanguages.filter((_lang, i) => i !== index);
		setResumeData((prevState) => {
			return {
				...prevState,
				extras: {
					...prevState.extras,
					languages: languages,
				},
			};
		});

		setUserLanguages(languages);
		setRowErrors((previous) => previous.filter((_error, i) => i !== index));
	};

	return (
		<>
			<Dialog open={showUnsavedChangesWarning} onOpenChange={setShowUnsavedChangesWarning}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>You have unsaved changes</DialogTitle>
						<DialogDescription>Are you sure you want to exit without save new changes?</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button type="button" variant="secondary" onClick={() => setShowUnsavedChangesWarning(false)}>
							Cancel
						</Button>
						<Button type="button" onClick={onPrev}>
							Exit
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<div className="min-h-screen">
				{serverErrors.map((item, index) => {
					return <ErrorToast error={item} key={index} />;
				})}

				<div className="w-full lg:max-w-2xl">
					<div className="mb-3 flex items-start justify-between gap-3">
						<p className="text-gray-700 text-sm">List the languages you speak and your proficiency level in each.</p>
						<Tips content={tips.languages} />
					</div>
					{userLanguages.map((item, index) => (
						<LanguageItem
							item={item}
							key={index}
							languages={languages}
							index={index}
							updateLanguagesData={updateLanguagesData}
							deleteLanguage={deleteLangHandler}
							error={rowErrors[index]}
						/>
					))}

					<button
						type="button"
						className="mb-4 flex cursor-pointer items-center gap-2 font-semibold text-primary-700"
						onClick={() => {
							setUserLanguages([...userLanguages, { language: "", proficiency: "" }]);
						}}
					>
						<Plus className="size-4" /> Add another language
					</button>

					<div className="flex items-center justify-end gap-2">
						<button
							type="button"
							name="back"
							onClick={() => {
								// The row this screen opens with is not "unsaved changes" — warn only
								// once the reader has typed into one, otherwise skipping an untouched
								// screen raises a dialog about work that does not exist.
								userLanguages.every(isBlank) ? onNext() : setShowUnsavedChangesWarning(true);
							}}
							className="cursor-pointer font-semibold text-primary-700"
						>
							Skip for now
						</button>
						<Button type="button" onClick={onNext} name="next">
							{patchSection.isPending ? (
								<>
									Loading <Spinner />
								</>
							) : (
								"Continue"
							)}
						</Button>
					</div>
				</div>
			</div>
		</>
	);
};

export default Languages;
